import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';

/**
 * KillSwitchButton — emergency control for an Agentlet.
 *
 * Per Design Principles §5.7. Two visual states:
 *   - Idle: outlined neutral pill with a green dot — "Kill switch ready"
 *   - Active: solid red pill with a pulsing red dot — "Kill switch ENGAGED"
 *
 * Clicking opens the ConfirmKillSwitchModal which requires the user to
 * type the agent name *exactly* before the Engage button is enabled.
 * This is the Wiz-style guardrail for irreversible actions.
 *
 * The button NEVER actually engages the kill switch — it just calls
 * `onConfirm(reason)` once the user has typed the agent name and
 * provided a reason. The caller does the actual platform call.
 *
 * @param {Object} props
 * @param {string}  props.agentName — used as the confirm-typed string
 * @param {boolean} props.isActive  — true when the kill switch is engaged
 * @param {(reason: string) => Promise<void>|void} props.onConfirm
 * @param {() => Promise<void>|void} [props.onDeactivate]
 *   Optional. When provided + isActive=true, an "Reactivate" button
 *   appears in the active-state confirmation flow.
 */
export function KillSwitchButton({ agentName, isActive, onConfirm, onDeactivate }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={isActive ? `Kill switch is ENGAGED for ${agentName}` : `Open kill switch confirmation for ${agentName}`}
        aria-pressed={isActive}
        className={
          'v2-focus-ring inline-flex items-center gap-2 px-3 h-8 rounded-v2-sm ' +
          'text-[12px] font-medium border ' +
          'transition-colors duration-v2 ease-v2 ' +
          (isActive
            ? 'bg-v2-brand-soft border-v2-brand text-v2-brand'
            : 'bg-v2-surface border-v2-border text-v2-ink-secondary hover:border-v2-brand hover:text-v2-brand hover:bg-v2-brand-soft')
        }
      >
        <span
          aria-hidden="true"
          className={
            'w-1.5 h-1.5 rounded-full ' +
            (isActive ? 'bg-v2-brand animate-pulse' : 'bg-v2-success')
          }
        />
        <span>{isActive ? 'Kill switch ENGAGED' : 'Kill switch ready'}</span>
      </button>

      <ConfirmKillSwitchModal
        isOpen={open}
        agentName={agentName}
        isActive={isActive}
        onClose={() => setOpen(false)}
        onConfirm={async (reason) => {
          await (onConfirm && onConfirm(reason));
          setOpen(false);
        }}
        onDeactivate={onDeactivate ? async () => {
          await onDeactivate();
          setOpen(false);
        } : undefined}
      />
    </>
  );
}

/**
 * ConfirmKillSwitchModal — type-to-confirm modal.
 *
 * Internal to KillSwitchButton, but exported for cases where the
 * caller wants to drive the modal directly (e.g. from a global
 * keyboard shortcut). The Engage button stays disabled until the
 * typed agent name matches and a reason is provided.
 */
export function ConfirmKillSwitchModal({ isOpen, agentName, isActive, onClose, onConfirm, onDeactivate }) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset on open
  React.useEffect(() => {
    if (isOpen) { setTyped(''); setReason(''); setBusy(false); }
  }, [isOpen]);

  const isMatch = typed.trim() === agentName;
  const canSubmit = isMatch && reason.trim().length > 0 && !busy;

  const handleEngage = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); }
    finally { setBusy(false); }
  };

  const handleDeactivate = async () => {
    if (!onDeactivate || busy) return;
    setBusy(true);
    try { await onDeactivate(); }
    finally { setBusy(false); }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isActive ? 'Kill switch is ENGAGED' : 'Engage kill switch?'}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        isActive ? (
          <>
            <Button variant="secondary" onClick={onClose}>Close</Button>
            {onDeactivate && (
              <Button variant="primary" loading={busy} onClick={handleDeactivate}>
                Reactivate {agentName}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              variant="danger"
              iconLeft={<AlertTriangle />}
              disabled={!canSubmit}
              loading={busy}
              onClick={handleEngage}
            >
              Engage Kill Switch
            </Button>
          </>
        )
      }
    >
      {isActive ? (
        <>
          <p className="text-[13px] text-v2-ink leading-relaxed">
            <strong>{agentName}</strong> is currently kill-switched. Every in-flight workflow has been paused.
          </p>
          <p className="text-[12px] text-v2-ink-muted mt-3">
            To reactivate, click the button below. Reactivation may require Platform Admin escalation
            depending on the Kill-Switch Cascade level that engaged it.
          </p>
        </>
      ) : (
        <>
          <p className="text-[13px] text-v2-ink leading-relaxed">
            Engaging the kill switch will <strong>immediately pause</strong> every in-flight workflow
            for <strong>{agentName}</strong>. Reactivation requires escalation to a higher cascade level.
          </p>
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1.5">
                Type <code className="font-mono-v2 text-v2-ink">{agentName}</code> to confirm
              </span>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                aria-label={`Type ${agentName} to confirm`}
                className={
                  'v2-focus-ring w-full bg-v2-surface text-v2-ink ' +
                  'border rounded-v2-sm px-3 py-2 text-[13px] font-mono-v2 ' +
                  (typed && !isMatch ? 'border-v2-danger' : 'border-v2-border')
                }
              />
              {typed && !isMatch && (
                <span className="block text-[11px] text-v2-danger mt-1">Name doesn't match.</span>
              )}
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1.5">
                Reason (recorded in audit trail)
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                aria-label="Reason for engaging kill switch"
                placeholder="e.g. PHI exfiltration detected on partner-llm-7"
                className={
                  'v2-focus-ring w-full bg-v2-surface text-v2-ink ' +
                  'border border-v2-border rounded-v2-sm px-3 py-2 text-[13px] resize-y'
                }
              />
            </label>
          </div>
        </>
      )}
    </Modal>
  );
}

export default KillSwitchButton;
